import {
	Currency,
	type FinancialPeriod,
	Formula,
	formatToDollar,
	type SuretyAccountId,
} from "@inrev/common";
import { Option } from "@inrev/core";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import {
	type Dispatch,
	type FC,
	type ReactNode,
	useCallback,
	useId,
	useMemo,
	useState,
} from "react";
import type { PeriodSelectorTableAction } from "../../financials/FinancialsPeriodSelectorDrawer";
import { PeriodChangeBadge } from "../../financials/PeriodChangeBadge";
import {
	hideEmptyRowsTableAction,
	ppChangeTableAction,
	usePeriodSelectorOverlay,
} from "../../financials/periodSelectorOverlay";
import { ConfirmationModal } from "../../layout/ConfirmationModal";
import { Icon } from "../../primitives";
import type {
	ColumnDef,
	ColumnGroupDef,
	DataGridColumnEntry,
	DataGridProps,
	DataGridRow,
	DataGridRowFormat,
} from "../DataGrid";
import {
	DataGrid,
	parseDraftToNumber,
	useDataGridExpansion,
	useDataGridExpansionDispatch,
} from "../DataGrid";
import { Tag } from "../Tag";
import { PortaledTooltip } from "../Tooltip";
import { GridViewToggle } from "./GridViewToggle";
import { PendingLineItemRow } from "./PendingLineItemRow";
import { PeriodHeader } from "./PeriodHeader";
import { RenameLabelInput } from "./RenameLabelInput";
import {
	FinancialGridStateProvider,
	useFinancialGridDispatch,
	useFinancialGridState,
} from "./state/FinancialGridStateProvider";
import type { CellError, FinancialAction } from "./state/reducer";
import { cellErrorKey } from "./state/reducer";
import type {
	AdjustmentOverrideArgs,
	BooleanCell,
	CreateRowArgs,
	DeleteRowArgs,
	FinancialStatementPeriodId,
	FinancialViewRow,
	GivenOverrideArgs,
	GridTableActionToggle,
	GridViewOption,
	NonEmptyArray,
	NormalizeAdjustment,
	PeriodViewModel,
	RenameRowArgs,
	RowKind,
	StatementMatrixViewModel,
	ViewMode,
} from "./types";
import { ALL_GRID_TABLE_ACTION_TOGGLES, ViewModeEnum } from "./types";
import { mapPeriodRecord } from "./utils/buildPeriodRecord";
import { collectInitialExpandedRowIds } from "./utils/collectInitialExpandedRowIds";
import { formatDeleteLineItemError } from "./utils/formatDeleteLineItemError";

type MergeWithEdits<TPeriodId extends FinancialStatementPeriodId> = (args: {
	viewModel: StatementMatrixViewModel<TPeriodId>;
}) => StatementMatrixViewModel<TPeriodId>;

const defaultNormalizeAdjustment: NormalizeAdjustment = (_rowId, draft) => Formula.parse(draft);

export type FinancialDataGridProps<
	TPeriodId extends FinancialStatementPeriodId = FinancialStatementPeriodId,
> = {
	accountId: SuretyAccountId;
	baseViewModel: StatementMatrixViewModel<TPeriodId>;
	mergeWithEdits: MergeWithEdits<TPeriodId>;
	expandableRowIds: string[];
	label: string;
	testId: string;
	emptyMessage: string;
	initialView?: ViewMode;
	onGivenOverride: (args: GivenOverrideArgs<TPeriodId>) => Promise<void>;
	onAdjustmentOverride: (args: AdjustmentOverrideArgs<TPeriodId>) => Promise<void>;
	onDeleteRow: (args: DeleteRowArgs) => Promise<void>;
	onCreateRow: (args: CreateRowArgs<TPeriodId>) => Promise<void>;
	onRenameRow: (args: RenameRowArgs) => Promise<void>;
	renderPeriodContextMenu?: (period: PeriodViewModel<TPeriodId>) => ReactNode;
	tableActionToggles?: readonly GridTableActionToggle[];
	viewOptions?: NonEmptyArray<GridViewOption>;
	// When true, all cell editing and add/delete line-item affordances are suppressed.
	readOnly?: boolean;
	// When true, each period renders a single unlabeled value column (no Given/Adj/Allowed split,
	// no value-label sub-header, and no view-mode toggle). Used by the analysis grid.
	singleValueColumn?: boolean;
	// Cmd/Ctrl+E behavior forwarded to the underlying DataGrid. Defaults to "toggle".
	expandHotkeyMode?: DataGridProps["expandHotkeyMode"];
	normalizeAdjustment?: NormalizeAdjustment;
};

type CommitCreateHandler = (
	label: string,
	valueDrafts: Record<FinancialStatementPeriodId, string>,
	adjustmentDrafts: Record<FinancialStatementPeriodId, string>,
) => void;

const NO_VALUE_MESSAGE = "Enter a value or adjustment for at least one period";

const rowKindToFormat = (kind: RowKind, level: number): DataGridRowFormat => {
	switch (kind) {
		case "category":
			return level <= 2 ? { tone: "primary", weight: "semibold" } : { tone: "primary" };
		case "subtotal":
		case "derived":
			return { surface: "light", tone: "primary", weight: "semibold", topBorder: true };
		case "label":
			return { tone: "tertiary", label: { labelStyle: "caption" } };
		case "memo":
			return { tone: "tertiary", label: { italic: true } };
		case "spacer":
			return { density: "compact" };
		case "data":
			return { tone: "primary", italic: true, weight: "light" };
		default:
			return { tone: "primary" };
	}
};

const isNavigableFinancialRow = <TPeriodId extends FinancialStatementPeriodId>(
	row: FinancialViewRow<TPeriodId>,
): boolean => {
	if (row.kind === "spacer" || row.kind === "label" || row.kind === "boolean") return false;
	if (row.kind === "derived" && row.booleanCells) return false;
	return true;
};

const subColCount = (mode: ViewMode): number =>
	mode === "all" ? 3 : mode === "given_allowed" ? 2 : 1;

const booleanTagCell = (
	periodId: string,
	field: "given" | "adjustment" | "allowed",
	value: boolean | null,
): ReactNode => (
	<div
		key={`bool-${field}-${periodId}`}
		data-testid={`bool-${field}-${periodId}`}
		className="flex items-center justify-end px-2 h-full"
	>
		{value === null ? (
			<span className="text-tertiary text-sm">--</span>
		) : (
			<Tag
				color={value ? "green" : "red"}
				border
				className="min-h-0 px-[7px] py-[2px] text-[11px] leading-none"
			>
				{value ? "Y" : "N"}
			</Tag>
		)}
	</div>
);

const renderBooleanCells = <TPeriodId extends FinancialStatementPeriodId>(
	periods: PeriodViewModel<TPeriodId>[],
	booleanCells: Record<TPeriodId, BooleanCell>,
	viewMode: ViewMode,
): ReactNode => (
	<>
		{periods.flatMap((period) => {
			const cell = booleanCells[period.id];
			const cells: ReactNode[] = [];
			if (viewMode !== "allowed_only") {
				cells.push(booleanTagCell(period.id, "given", cell?.given ?? null));
			}
			if (viewMode === "all") {
				cells.push(booleanTagCell(period.id, "adjustment", null));
			}
			cells.push(booleanTagCell(period.id, "allowed", cell?.allowed ?? null));
			return cells;
		})}
	</>
);

const buildPendingRow = <TPeriodId extends FinancialStatementPeriodId>(
	parentRowId: string,
	periods: PeriodViewModel<TPeriodId>[],
	viewMode: ViewMode,
	onCommitCreate: CommitCreateHandler,
): DataGridRow<FinancialViewRow<TPeriodId>> => ({
	id: `pending::${parentRowId}`,
	label: "",
	navigable: false,
	renderRowBody: () => (
		<PendingLineItemRow periods={periods} viewMode={viewMode} onCommitCreate={onCommitCreate} />
	),
});

const AddLineItemButton: FC<{ rowId: string; rowLabel: string }> = ({ rowId, rowLabel }) => {
	const financialDispatch = useFinancialGridDispatch();
	const { expanded } = useDataGridExpansion();
	const { toggleRow } = useDataGridExpansionDispatch();

	return (
		<button
			type="button"
			onClick={() => {
				if (!expanded.has(rowId)) toggleRow(rowId);
				financialDispatch({ type: "start_create_row", parentRowId: rowId });
			}}
			aria-label={`Add line item to ${rowLabel}`}
			data-testid={`add-line-item-button-${rowId}`}
			className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-5 h-5 rounded-sm hover:bg-surface-light"
		>
			<Icon type="plus" size="mini" color="brand" />
		</button>
	);
};

type BuildRowArgs<TPeriodId extends FinancialStatementPeriodId> = {
	row: FinancialViewRow<TPeriodId>;
	periods: PeriodViewModel<TPeriodId>[];
	viewMode: ViewMode;
	financialDispatch: Dispatch<FinancialAction<TPeriodId>>;
	editState: ReturnType<typeof useFinancialGridState>;
	parentAllowsAddedLineItems?: boolean;
	rowError: string | null;
	readOnly: boolean;
	onRequestDelete: (args: DeleteRowArgs) => void;
	onCommitCreate: CommitCreateHandler;
	onCommitRename: (args: RenameRowArgs) => void;
};

const buildDataGridRow = <TPeriodId extends FinancialStatementPeriodId>({
	row,
	periods,
	viewMode,
	financialDispatch,
	editState,
	parentAllowsAddedLineItems = false,
	rowError,
	readOnly,
	onRequestDelete,
	onCommitCreate,
	onCommitRename,
}: BuildRowArgs<TPeriodId>): DataGridRow<FinancialViewRow<TPeriodId>> => {
	const cols = subColCount(viewMode);
	const isRemovableLineItem = !readOnly && row.kind === "data" && parentAllowsAddedLineItems;
	const isCreatingChildHere =
		editState.edit.kind === "creating_row" && editState.edit.parentRowId === row.id;
	const isEditingLabelHere =
		editState.edit.kind === "editing_label" && editState.edit.rowId === row.id;

	const labelActions: ReactNode = isRemovableLineItem ? (
		<span className="ml-auto flex items-center gap-1">
			<button
				type="button"
				onClick={() =>
					financialDispatch({ type: "start_rename_row", rowId: row.id, labelDraft: row.label })
				}
				aria-label={`Rename ${row.label}`}
				data-testid={`rename-line-item-${row.id}`}
				className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-5 h-5 rounded-sm hover:bg-surface-light"
			>
				<Icon type="pencil" size="mini" color="tertiary" />
			</button>
			<button
				type="button"
				onClick={() => onRequestDelete({ rowId: row.id, label: row.label })}
				aria-label={`Delete ${row.label}`}
				data-testid={`delete-line-item-${row.id}`}
				className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-5 h-5 rounded-sm hover:bg-surface-light"
			>
				<Icon type="trash" size="mini" color="danger" />
			</button>
		</span>
	) : !readOnly && row.allowsAddedLineItems ? (
		<AddLineItemButton rowId={row.id} rowLabel={row.label} />
	) : undefined;

	const booleanCells = row.booleanCells;
	const renderCells: (() => ReactNode) | undefined =
		row.kind === "spacer" || row.kind === "label"
			? () => <div style={{ gridColumn: `span ${periods.length * cols}` }} />
			: (row.kind === "boolean" || row.kind === "derived") && booleanCells
				? () => renderBooleanCells(periods, booleanCells, viewMode)
				: undefined;

	const existingChildren = row.children ?? [];
	const hasExistingChildren = existingChildren.length > 0;

	let children: DataGridRow<FinancialViewRow<TPeriodId>>[] | undefined;
	if (row.allowsAddedLineItems) {
		if (hasExistingChildren || isCreatingChildHere) {
			const dataChildren = existingChildren.filter((c) => c.kind === "data");
			const subtotalChild = existingChildren.find((c) => c.kind === "subtotal");
			children = [
				...dataChildren.map((child) =>
					buildDataGridRow({
						row: child,
						periods,
						viewMode,
						financialDispatch,
						editState,
						parentAllowsAddedLineItems: true,
						rowError,
						readOnly,
						onRequestDelete,
						onCommitCreate,
						onCommitRename,
					}),
				),
				...(isCreatingChildHere
					? [buildPendingRow(row.id, periods, viewMode, onCommitCreate)]
					: []),
				...(subtotalChild
					? [
							buildDataGridRow({
								row: subtotalChild,
								periods,
								viewMode,
								financialDispatch,
								editState,
								rowError,
								readOnly,
								onRequestDelete,
								onCommitCreate,
								onCommitRename,
							}),
						]
					: []),
			];
		}
	} else if (hasExistingChildren) {
		children = existingChildren.map((child) =>
			buildDataGridRow({
				row: child,
				periods,
				viewMode,
				financialDispatch,
				editState,
				rowError,
				readOnly,
				onRequestDelete,
				onCommitCreate,
				onCommitRename,
			}),
		);
	}

	const format = rowKindToFormat(row.kind, row.level);

	return {
		id: row.id,
		label: row.label,
		format,
		navigable: isNavigableFinancialRow(row),
		hasError: row.id === rowError,
		alwaysExpanded: row.alwaysExpanded,
		hideValuesWhenExpanded: row.hideValuesWhenExpanded,
		indentOffset: row.indentOffset,
		labelActions,
		renderLabel: isEditingLabelHere
			? () => (
					<RenameLabelInput
						rowId={row.id}
						onCommit={(newLabel) =>
							onCommitRename({ rowId: row.id, oldLabel: row.label, newLabel })
						}
					/>
				)
			: undefined,
		renderCells,
		data: row,
		children,
	};
};

// left must match the sticky label column's rendered width (it caps at 360px) so
// scrollIntoView clears it — a smaller value leaves the first column half-occluded
// when navigating back left after a horizontal scroll.
const FINANCIAL_GRID_SCROLL_PADDING = { left: 360, right: 128 };

const EMPTY_ROW_KINDS = new Set<RowKind>(["data", "editable", "memo"]);

const isLeafContentRow = <TPeriodId extends FinancialStatementPeriodId>(
	row: FinancialViewRow<TPeriodId>,
): boolean =>
	EMPTY_ROW_KINDS.has(row.kind) || (row.kind === "category" && !!row.allowsAddedLineItems);

const isRowEffectivelyEmpty = <TPeriodId extends FinancialStatementPeriodId>(
	row: FinancialViewRow<TPeriodId>,
	periodIds: TPeriodId[],
): boolean => {
	// Leaf group rows on the balance sheet (category + allowsAddedLineItems) are
	// filterable — they're always created so admins can add line items to them,
	// but should be hideable via the toggle when they have no data.
	if (!isLeafContentRow(row)) return false;
	return periodIds.every((id) => {
		const cell = row.cells[id];
		return (cell?.allowed ?? null) === null && (cell?.displayValue ?? null) === null;
	});
};

// Returns true if the (already-filtered) subtree contains at least one leaf content row.
const hasLeafContent = <TPeriodId extends FinancialStatementPeriodId>(
	rows: FinancialViewRow<TPeriodId>[],
): boolean =>
	rows.some(
		(row) => isLeafContentRow(row) || (row.children !== undefined && hasLeafContent(row.children)),
	);

// Map (recurse) before filtering so that the cascade check below operates on
// already-pruned children. A structural parent (category without
// allowsAddedLineItems) is hidden when all of its descendant leaf rows have
// already been filtered out.
//
// Structural rows at L1/L2 (sections and categories, e.g. Assets > Current
// Assets) always remain visible, even when all of their line items are empty
// and pruned away. User-added line items (kind "data") are filterable at any
// level — income statement revenue line items live at L2.
const filterEmptyRows = <TPeriodId extends FinancialStatementPeriodId>(
	rows: FinancialViewRow<TPeriodId>[],
	periodIds: TPeriodId[],
): FinancialViewRow<TPeriodId>[] =>
	rows
		.map((row) =>
			row.children ? { ...row, children: filterEmptyRows(row.children, periodIds) } : row,
		)
		.filter((row) => {
			if (row.level <= 2 && row.kind !== "data") return true;
			return (
				!isRowEffectivelyEmpty(row, periodIds) &&
				(row.children === undefined || hasLeafContent(row.children))
			);
		});

type FinancialDataGridInnerProps<TPeriodId extends FinancialStatementPeriodId> = Omit<
	FinancialDataGridProps<TPeriodId>,
	"initialView" | "onGivenOverride" | "onAdjustmentOverride"
>;

const FinancialDataGridInner = <TPeriodId extends FinancialStatementPeriodId>({
	accountId,
	baseViewModel,
	mergeWithEdits,
	expandableRowIds,
	label,
	testId,
	emptyMessage,
	renderPeriodContextMenu,
	onDeleteRow,
	onCreateRow,
	onRenameRow,
	tableActionToggles = ALL_GRID_TABLE_ACTION_TOGGLES,
	viewOptions,
	readOnly = false,
	singleValueColumn,
	expandHotkeyMode,
	normalizeAdjustment = defaultNormalizeAdjustment,
}: FinancialDataGridInnerProps<TPeriodId>) => {
	const state = useFinancialGridState();
	const dispatch = useFinancialGridDispatch<TPeriodId>();
	const { showErrorFlag } = useFlags();
	const formulaTooltipId = useId();
	const [pendingDelete, setPendingDelete] = useState<DeleteRowArgs | null>(null);

	const headerActions = useMemo(
		() => (singleValueColumn ? null : <GridViewToggle viewOptions={viewOptions} />),
		[singleValueColumn, viewOptions],
	);

	// The drawer is grid-agnostic: it renders whatever toggles it is given. Map this grid's enabled
	// toggles to concrete actions backed by the financial grid state.
	const tableActions = useMemo<PeriodSelectorTableAction[]>(() => {
		const actions: PeriodSelectorTableAction[] = [];
		if (tableActionToggles.includes("ppChange")) {
			actions.push(
				ppChangeTableAction(state.showPpChange, () => dispatch({ type: "toggle_pp_change" })),
			);
		}
		if (tableActionToggles.includes("hideEmptyRows")) {
			actions.push(
				hideEmptyRowsTableAction(state.hideEmptyRows, () =>
					dispatch({ type: "toggle_hide_empty_rows" }),
				),
			);
		}
		return actions;
	}, [tableActionToggles, state.showPpChange, state.hideEmptyRows, dispatch]);

	const { periodSelectorHeader, periodSelectorOverlay } = usePeriodSelectorOverlay({
		accountId,
		tableActions,
		expandableRowIds,
		showFullLabel: singleValueColumn,
		headerActions,
	});

	const handleCommitCreate = useCallback<CommitCreateHandler>(
		(label, valueDrafts, adjustmentDrafts) => {
			if (state.edit.kind !== "creating_row") return;
			const { parentRowId } = state.edit;
			const trimmedLabel = label.trim();
			if (trimmedLabel === "") {
				dispatch({ type: "cancel_create_row" });
				return;
			}
			const valuesByPeriod = mapPeriodRecord(valueDrafts, (_, draft) =>
				parseDraftToNumber(draft).getOrElse(null),
			);
			const adjustmentsByPeriod = mapPeriodRecord(adjustmentDrafts, (_, draft) =>
				draft.trim() === ""
					? null
					: Formula.parse(draft).match(
							(formula) => formula,
							() => null,
						),
			);
			const hasAnyValue = Object.values(valuesByPeriod).some((value) => value !== null);
			const hasAnyAdjustment = Object.values(adjustmentsByPeriod).some(
				(adjustment) => adjustment !== null,
			);
			if (!hasAnyValue && !hasAnyAdjustment) {
				dispatch({ type: "set_create_form_error", message: NO_VALUE_MESSAGE });
				showErrorFlag({ heading: "Add Line Item", message: NO_VALUE_MESSAGE });
				return;
			}
			dispatch({ type: "cancel_create_row" });
			void onCreateRow({
				parentRowId,
				label: trimmedLabel,
				valuesByPeriod,
				adjustmentsByPeriod,
			}).catch((error: unknown) => {
				showErrorFlag({
					heading: "Error Adding Line Item",
					message: error instanceof Error ? error.message : "Failed to add line item",
				});
				dispatch({
					type: "start_create_row",
					parentRowId,
					labelDraft: label,
					valueDrafts,
					adjustmentDrafts,
				});
			});
		},
		[state.edit, dispatch, onCreateRow, showErrorFlag],
	);

	const requestDelete = useCallback(
		(args: DeleteRowArgs) => {
			dispatch({ type: "clear_row_error", rowId: args.rowId });
			setPendingDelete(args);
		},
		[dispatch],
	);

	const handleCommitRename = useCallback(
		(args: RenameRowArgs) => {
			const newLabel = args.newLabel.trim();
			if (newLabel === "" || newLabel === args.oldLabel) {
				dispatch({ type: "cancel_rename_row" });
				return;
			}
			dispatch({ type: "cancel_rename_row" });
			void onRenameRow({ ...args, newLabel }).catch((error: unknown) => {
				showErrorFlag({
					heading: "Error Renaming Line Item",
					message: error instanceof Error ? error.message : "Failed to rename line item",
				});
				dispatch({ type: "start_rename_row", rowId: args.rowId, labelDraft: newLabel });
			});
		},
		[dispatch, onRenameRow, showErrorFlag],
	);

	const mergedViewModel = useMemo(
		() => mergeWithEdits({ viewModel: baseViewModel }),
		[baseViewModel, mergeWithEdits],
	);

	const viewMode = singleValueColumn ? ViewModeEnum.AllowedOnly : state.view;

	// Empty-row filtering is user-controllable only when the grid exposes the
	// hideEmptyRows toggle; grids without the toggle always hide empty rows.
	const hideEmptyRows = tableActionToggles.includes("hideEmptyRows") ? state.hideEmptyRows : true;

	const viewModel = useMemo(() => {
		if (!hideEmptyRows) return mergedViewModel;
		const periodIds = mergedViewModel.periods.map((p) => p.id);
		return { ...mergedViewModel, rows: filterEmptyRows(mergedViewModel.rows, periodIds) };
	}, [mergedViewModel, hideEmptyRows]);

	const columns: DataGridColumnEntry<FinancialViewRow<TPeriodId>>[] = useMemo(
		() =>
			viewModel.periods.map(
				(period, index): ColumnGroupDef<FinancialViewRow<TPeriodId>> => ({
					id: period.id,
					label: period.label,
					subLabel: period.periodEndDate,
					renderGroupHeader: () => (
						<PeriodHeader period={period} contextMenu={renderPeriodContextMenu?.(period)} />
					),
					columns: buildPeriodColumns(
						period,
						viewMode,
						dispatch,
						state.cellErrors,
						formulaTooltipId,
						state.showPpChange,
						readOnly,
						normalizeAdjustment,
						singleValueColumn,
						viewModel.periods[index + 1],
					),
				}),
			),
		[
			viewModel.periods,
			viewMode,
			dispatch,
			state.cellErrors,
			formulaTooltipId,
			state.showPpChange,
			readOnly,
			normalizeAdjustment,
			singleValueColumn,
			renderPeriodContextMenu,
		],
	);

	const dataGridRows: DataGridRow<FinancialViewRow<TPeriodId>>[] = useMemo(() => {
		if (viewModel.periods.length === 0) return [];
		return viewModel.rows.map((row) =>
			buildDataGridRow({
				row,
				periods: viewModel.periods,
				viewMode,
				financialDispatch: dispatch,
				editState: state,
				rowError: state.rowError,
				readOnly,
				onRequestDelete: requestDelete,
				onCommitCreate: handleCommitCreate,
				onCommitRename: handleCommitRename,
			}),
		);
	}, [
		viewModel.rows,
		viewModel.periods,
		viewMode,
		dispatch,
		state.edit,
		state.rowError,
		readOnly,
		requestDelete,
		handleCommitCreate,
		handleCommitRename,
	]);

	const initialExpandedRowIds = useMemo(
		() => collectInitialExpandedRowIds(viewModel.rows),
		[viewModel.rows],
	);

	return (
		<>
			<DataGrid
				columns={columns}
				rows={dataGridRows}
				labelColumn={{
					label,
					renderHeader: periodSelectorHeader,
				}}
				overlay={periodSelectorOverlay}
				testId={testId}
				emptyMessage={emptyMessage}
				initialExpanded={initialExpandedRowIds}
				scrollPadding={FINANCIAL_GRID_SCROLL_PADDING}
				rowHeight={26}
				hideGroupSubHeader={singleValueColumn}
				expandHotkeyMode={expandHotkeyMode}
			/>
			<PortaledTooltip id={formulaTooltipId} delayShow={200} place="top" />
			{pendingDelete && (
				<ConfirmationModal
					message="Delete this line item from all selected periods?"
					subMessage="This action cannot be undone."
					confirmButtonText="Delete"
					confirmationButtonColor="danger"
					onCancel={() => setPendingDelete(null)}
					onConfirm={() => {
						const deleteArgs = pendingDelete;
						void onDeleteRow(deleteArgs)
							.then(() => {
								dispatch({ type: "clear_row_error", rowId: deleteArgs.rowId });
							})
							.catch((error: unknown) => {
								dispatch({ type: "set_row_error", rowId: deleteArgs.rowId });
								showErrorFlag({
									heading: "Error Deleting Line Item",
									message: formatDeleteLineItemError(deleteArgs.label, error),
									onDismiss: () => dispatch({ type: "clear_row_error", rowId: deleteArgs.rowId }),
								});
							})
							.finally(() => {
								setPendingDelete(null);
							});
					}}
				/>
			)}
		</>
	);
};

const isRowEditable = <TPeriodId extends FinancialStatementPeriodId>(
	row: DataGridRow<FinancialViewRow<TPeriodId>>,
): boolean => {
	if (row.data?.editable === false) return false;
	return row.data?.kind === "editable" || row.data?.kind === "memo" || row.data?.kind === "data";
};

const formatSignedAdjustment = (adj: FinancialPeriod.AdjustedLeaf): string =>
	formatToDollar(adj.value, { accounting: true, round: true, omitSymbol: true }) ?? "";

const formatGivenValue = (value: number | null | undefined): string =>
	value === null || value === undefined
		? "--"
		: (formatToDollar(value, { accounting: true, round: true, omitSymbol: true }) ?? "");

const CELL_CONTAINER_CLASS = "flex items-center justify-end gap-1 pr-2 h-full w-full";

// Shared value-cell typography: legacy financials mono font with slashed zeros and
// tabular figures so digits align column-to-column.
const VALUE_TEXT_CLASS = "text-sm tabular-nums";

// All period columns (Given/Adj/Allowed) share one fixed width so they render at identical
// widths and stay bounded even with a single period (the label column caps at 360px).
const PERIOD_COLUMN_WIDTH = "144px";

const ParseErrorCell: FC<{ message: string; tooltipId: string }> = ({ message, tooltipId }) => (
	<div className={CELL_CONTAINER_CLASS} data-tooltip-id={tooltipId} data-tooltip-content={message}>
		<span className="text-xs font-medium text-danger tabular-nums">ERR</span>
	</div>
);

const SaveErrorCell: FC<{ message: string; tooltipId: string; children: ReactNode }> = ({
	message,
	tooltipId,
	children,
}) => (
	<div
		className="flex items-center justify-end h-full w-full border border-danger rounded-sm"
		data-tooltip-id={tooltipId}
		data-tooltip-content={message}
		data-save-error="true"
	>
		{children}
	</div>
);

const buildPeriodColumns = <TPeriodId extends FinancialStatementPeriodId>(
	period: PeriodViewModel<TPeriodId>,
	viewMode: ViewMode,
	financialDispatch: Dispatch<FinancialAction<TPeriodId>>,
	cellErrors: Map<string, CellError>,
	tooltipId: string,
	showPpChange: boolean,
	readOnly: boolean,
	normalizeAdjustment: NormalizeAdjustment,
	hideValueLabel?: boolean,
	compareToPeriod?: PeriodViewModel<TPeriodId>,
): ColumnDef<FinancialViewRow<TPeriodId>>[] => {
	const cols: ColumnDef<FinancialViewRow<TPeriodId>>[] = [];

	if (viewMode !== "allowed_only") {
		cols.push({
			id: `${period.id}::given`,
			label: "Given",
			width: PERIOD_COLUMN_WIDTH,
			navigable: true,
			editable: (row) => !readOnly && isRowEditable(row),
			inputMode: "decimal",
			visible: true,
			renderCell: (row) => {
				const error = cellErrors.get(cellErrorKey(row.id, period.id, "given"));
				if (error?.kind === "parse") {
					return <ParseErrorCell message={error.message} tooltipId={tooltipId} />;
				}
				const cell = row.data?.cells[period.id];
				const displayValue =
					error?.kind === "save"
						? parseDraftToNumber(error.draft).match(
								(value) => formatGivenValue(value),
								() => error.draft,
							)
						: formatGivenValue(cell?.given);
				const valueSpan = (
					<span className={`${VALUE_TEXT_CLASS} ml-auto pr-2`}>{displayValue}</span>
				);
				if (error?.kind === "save") {
					return (
						<SaveErrorCell message={error.message} tooltipId={tooltipId}>
							{valueSpan}
						</SaveErrorCell>
					);
				}
				return valueSpan;
			},
			getEditValue: (row) => {
				const error = cellErrors.get(cellErrorKey(row.id, period.id, "given"));
				if (error) return error.draft;
				const cell = row.data?.cells[period.id];
				return cell?.given !== null && cell?.given !== undefined ? String(cell.given) : "";
			},
			onCancel: (row) => {
				financialDispatch({
					type: "clear_save_error",
					rowId: row.id,
					periodId: period.id,
					field: "given",
				});
			},
			onCommit: (row, draft) => {
				parseDraftToNumber(draft).match(
					(value) => {
						financialDispatch({
							type: "set_given_override",
							rowId: row.id,
							periodId: period.id,
							value,
							draft,
						});
					},
					(error) => {
						financialDispatch({
							type: "set_given_error",
							rowId: row.id,
							periodId: period.id,
							message: error.message,
							draft,
						});
					},
				);
			},
		});
	}

	if (viewMode === "all") {
		cols.push({
			id: `${period.id}::adjustment`,
			label: "Adj",
			width: PERIOD_COLUMN_WIDTH,
			navigable: true,
			editable: (row) => !readOnly && isRowEditable(row),
			inputMode: "decimal",
			inputPlaceholder: "+0",
			visible: true,
			renderCell: (row) => {
				const cell = row.data?.cells[period.id];
				const adj = Option.toNull(cell?.adjustment);
				const formula = adj?.formula;
				const isAutoApplied = adj?.createdByUserId === null && isRowEditable(row);
				const error = cellErrors.get(cellErrorKey(row.id, period.id, "adjustment"));
				if (error?.kind === "parse") {
					return <ParseErrorCell message={error.message} tooltipId={tooltipId} />;
				}
				if (error?.kind === "save") {
					return (
						<SaveErrorCell message={error.message} tooltipId={tooltipId}>
							<div className={CELL_CONTAINER_CLASS}>
								<span className={VALUE_TEXT_CLASS}>
									{error.draft.trim() === "" ? "--" : error.draft}
								</span>
							</div>
						</SaveErrorCell>
					);
				}
				return (
					<div
						className={CELL_CONTAINER_CLASS}
						{...(Option.isSome(formula)
							? { "data-tooltip-id": tooltipId, "data-tooltip-content": formula }
							: {})}
					>
						{isAutoApplied && <span className="size-1 rounded-full bg-brand-accent shrink-0" />}
						<span className={VALUE_TEXT_CLASS}>{adj ? formatSignedAdjustment(adj) : "--"}</span>
					</div>
				);
			},
			getEditValue: (row) => {
				const error = cellErrors.get(cellErrorKey(row.id, period.id, "adjustment"));
				if (error) return error.draft;
				const cell = row.data?.cells[period.id];
				if (!cell?.adjustment) return "";
				return cell.adjustment.formula;
			},
			onCancel: (row) => {
				financialDispatch({
					type: "clear_save_error",
					rowId: row.id,
					periodId: period.id,
					field: "adjustment",
				});
			},
			onCommit: (row, draft) => {
				if (draft.trim() === "") {
					financialDispatch({
						type: "set_adjustment_override",
						rowId: row.id,
						periodId: period.id,
						value: null,
						draft,
					});
					return;
				}

				normalizeAdjustment(row.id, draft).match(
					(formula) => {
						const g = row.data?.cells[period.id]?.given ?? 0;
						const value = Formula.evaluate(g, formula);
						if (!Number.isFinite(value)) {
							financialDispatch({
								type: "set_adjustment_error",
								rowId: row.id,
								periodId: period.id,
								message: "Result is not a finite number",
								draft,
							});
							return;
						}
						financialDispatch({
							type: "set_adjustment_override",
							rowId: row.id,
							periodId: period.id,
							value: Currency.round(value),
							formula: formula,
							draft,
						});
					},
					(error) => {
						financialDispatch({
							type: "set_adjustment_error",
							rowId: row.id,
							periodId: period.id,
							message: error.message,
							draft,
						});
					},
				);
			},
		});
	}

	cols.push({
		id: `${period.id}::allowed`,
		label: hideValueLabel ? "" : "Allowed",
		width: PERIOD_COLUMN_WIDTH,
		navigable: true,
		visible: true,
		renderCell: (row) => {
			const cell = row.data?.cells[period.id];
			const allowedValue = cell?.allowed ?? null;
			const compareValue =
				showPpChange && compareToPeriod
					? (row.data?.cells[compareToPeriod.id]?.allowed ?? null)
					: null;
			const rowColorMode = row.data?.ppChangeColorMode ?? "higherIsBetter";
			const colorMode =
				rowColorMode === "neutral"
					? "neutral"
					: cell?.valueKind === "days"
						? "lowerIsBetter"
						: rowColorMode;

			return (
				<div className="flex items-center justify-end gap-1.5 pr-2 h-full w-full">
					<PeriodChangeBadge
						value={allowedValue}
						prevValue={compareValue}
						type={cell?.valueKind}
						colorMode={colorMode}
					/>
					<span className={VALUE_TEXT_CLASS}>
						{cell?.displayValue ?? formatGivenValue(allowedValue)}
					</span>
				</div>
			);
		},
	});

	return cols;
};

export const FinancialDataGrid = <
	TPeriodId extends FinancialStatementPeriodId = FinancialStatementPeriodId,
>({
	initialView = "all",
	onGivenOverride,
	onAdjustmentOverride,
	...props
}: FinancialDataGridProps<TPeriodId>) => (
	<FinancialGridStateProvider<TPeriodId>
		initialView={initialView}
		onGivenOverride={onGivenOverride}
		onAdjustmentOverride={onAdjustmentOverride}
	>
		<FinancialDataGridInner {...props} />
	</FinancialGridStateProvider>
);
